/**
 * BPMN 属性面板组件
 * 
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';
import {
  Form,
  Button,
  Space,
  Input,
  message
} from 'antd';
import {
  SaveOutlined,
  DeleteOutlined,
  SettingOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import BasicProperties from './BasicProperties';
import UserTaskProperties from './UserTaskProperties';
import ScriptTaskProperties from './ScriptTaskProperties';
import ServiceTaskProperties from './ServiceTaskProperties';
import SequenceFlowProperties from './SequenceFlowProperties';
import FormSelector from '../FormSelector';
import UserAssignmentSelector from '../UserAssignmentSelector';
import useRequest from '../../../utils/useRequest';



// BPMN 元素类型配置
const ELEMENT_CONFIGS = {
  'start-event': {
    name: '开始事件',
    color: '#52c41a',
    properties: ['basic']
  },
  'end-event': {
    name: '结束事件',
    color: '#ff4d4f',
    properties: ['basic']
  },
  'user-task': {
    name: '用户任务',
    color: '#1890ff',
    properties: ['basic', 'assignment']
  },
  'service-task': {
    name: '服务任务',
    color: '#52c41a',
    properties: ['basic']
  },
  'script-task': {
    name: '脚本任务',
    color: '#fa8c16',
    properties: ['basic', 'script']
  },
  'exclusive-gateway': {
    name: '排他网关',
    color: '#faad14',
    properties: ['basic']
  },
  'parallel-gateway': {
    name: '并行网关',
    color: '#52c41a',
    properties: ['basic']
  },
  'inclusive-gateway': {
    name: '包容网关',
    color: '#1890ff',
    properties: ['basic']
  },
  'sequence-flow': {
    name: '连线',
    color: '#722ed1',
    properties: ['basic', 'condition']
  }
};

const BpmnPropertyPanel = ({ selectedElement, onUpdateElement, onDeleteElement }) => {
  // 所有 Hooks 必须在组件顶部调用
  const { id: processKey } = useParams();
  const { request } = useRequest();
  const [form] = Form.useForm();
  const [formSelectorVisible, setFormSelectorVisible] = useState(false);
  const [selectedForms, setSelectedForms] = useState([]);
  const [userAssignmentVisible, setUserAssignmentVisible] = useState(false);
  const [assignmentConfig, setAssignmentConfig] = useState(null);
  const [taskSchedule, setTaskSchedule] = useState({
    due_date: null,
    follow_up_date: null
  });
  
  // useEffect 必须在条件渲染之前调用
  useEffect(() => {
    if (selectedElement) {
      const initialValues = {
        id: selectedElement.id,
        name: selectedElement.customData?.name || selectedElement.customData?.bpmnName || '',
        documentation: selectedElement.customData?.documentation || '',
        formKey: selectedElement.customData?.formKey || '',
        script: selectedElement.customData?.script || '',
        scriptFormat: selectedElement.customData?.scriptFormat || 'javascript',
        due_date: selectedElement.customData?.due_date ? dayjs(selectedElement.customData.due_date) : null,
        follow_up_date: selectedElement.customData?.follow_up_date ? dayjs(selectedElement.customData.follow_up_date) : null,
        // 连线条件表达式相关字段
        conditionExpression: selectedElement.customData?.conditionExpression || ''
      };
      
      form.setFieldsValue(initialValues);
      
      // 初始化任务调度配置
      setTaskSchedule({
        due_date: selectedElement.customData?.due_date || null,
        follow_up_date: selectedElement.customData?.follow_up_date || null
      });
      
      // 初始化分配配置
      setAssignmentConfig(selectedElement.customData?.assignmentConfig || null);
    }
  }, [selectedElement, form]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      
      // 调试日志：检查表单值
      console.log('表单验证后的值:', values);
      console.log('formKey值:', values.formKey);
      console.log('当前表单字段值:', form.getFieldsValue());
      
      const elementType = selectedElement.customData?.bpmnType;
      
      // 根据元素类型构建不同的保存数据结构
      if (elementType === 'sequence-flow') {
        // 连线元素的保存逻辑
        const sequenceFlowData = {
          element_id: selectedElement.id,
          element_type: 'sequence-flow',
          properties: {
            name: values.name,
            documentation: values.documentation,
            condition_expression: values.conditionExpression
          }
        };
        
        console.log('保存连线配置到后端:', sequenceFlowData);
        
        const apiUrl = `http://127.0.0.1:8080/api/v1/processes/${processKey}/elements/${selectedElement.id}/config`;
        const response = await request(apiUrl, 'POST', 'json', sequenceFlowData);

        if (!response || !response.success) {
          throw new Error(response?.message || '保存连线配置失败');
        }
        
        // 更新本地元素数据
        const updatedElement = {
          ...selectedElement,
          customData: {
            ...selectedElement.customData,
            ...values
          }
        };
        
        onUpdateElement(updatedElement);
        message.success('连线配置保存成功');
        
      } else {
        // 任务元素的保存逻辑
        const saveData = {
          task_id: selectedElement.id,
          guanshi_config: {
            assignment_definition: {
              candidate: JSON.stringify(assignmentConfig || {
                assignee: values.assignee,
                candidateUsers: values.candidateUsers || [],
                candidateGroups: values.candidateGroups || []
              })
            },
            task_schedule: {
              due_date: taskSchedule.due_date,
              follow_up_date: taskSchedule.follow_up_date
            },
            form_definition: {
              external_reference: JSON.stringify(values.formKey || null)
            }
          }
        };
        
        // 调用后端API保存任务配置
        const apiUrl = `http://127.0.0.1:8080/api/v1/processes/${processKey}/tasks/${selectedElement.id}/config`;
        
        console.log('保存任务配置到后端:', saveData);
        console.log('API URL:', apiUrl);
        
        // 实际的API调用
        const response = await request(apiUrl, 'POST', 'json', saveData);
        
        if (!response || !response.success) {
          throw new Error(response?.message || '保存失败');
        }
        
        // 更新本地元素数据
        const updatedElement = {
          ...selectedElement,
          customData: {
            ...selectedElement.customData,
            ...values,
            assignmentConfig,
            due_date: taskSchedule.due_date,
            follow_up_date: taskSchedule.follow_up_date,
            taskConfig: saveData // 保存完整的任务配置
          }
        };
        
        // 更新本地状态
        onUpdateElement(updatedElement);
        
        console.log('任务配置已保存');
        message.success('任务配置保存成功');
      }
      
    } catch (error) {
      console.error('保存失败:', error);
      message.error(`保存失败: ${error.message || '未知错误'}`);
    }
  }, [processKey, request, form, selectedElement, onUpdateElement, assignmentConfig, taskSchedule]);

  const handleDelete = useCallback(() => {
    if (selectedElement && onDeleteElement) {
      // TODO: 实现删除确认对话框
      // TODO: 实现删除后端数据的 API 调用
      console.log('占位符: 删除元素', selectedElement.id);
      
      onDeleteElement(selectedElement.id);
      
      // TODO: 添加删除成功提示
      console.log('元素已删除');
    }
  }, [selectedElement, onDeleteElement]);

  // 获取元素配置
  const elementConfig = selectedElement 
    ? (ELEMENT_CONFIGS[selectedElement.customData?.bpmnType] || {
        name: '未知元素',
        color: '#666',
        properties: ['basic']
      })
    : null;

  // 条件渲染必须在所有 Hooks 之后
  if (!selectedElement) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            属性面板
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-gray-500">
            <SettingOutlined className="text-4xl mb-2" />
            <div>选择一个元素以查看其属性</div>
          </div>
        </div>
      </div>
    );
  }

  // 渲染所有属性内容
  const renderAllProperties = () => {
    const elementType = selectedElement.customData?.bpmnType;
    const properties = elementConfig.properties || ['basic'];
    
    return (
      <div className="space-y-0">
        {/* 隐藏的formKey字段，用于存储表单关联信息 */}
        <Form.Item name="formKey" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
        
        {/* 基础属性 */}
        {properties.includes('basic') && (
          <BasicProperties 
            form={form}
            selectedElement={selectedElement}
          />
        )}
        
        {/* 用户任务属性 */}
        {elementType === 'user-task' && (
          <UserTaskProperties 
            form={form}
            assignmentConfig={assignmentConfig}
            setAssignmentConfig={setAssignmentConfig}
            taskSchedule={taskSchedule}
            setTaskSchedule={setTaskSchedule}
            setFormSelectorVisible={setFormSelectorVisible}
            setUserAssignmentVisible={setUserAssignmentVisible}
          />
        )}
        
        {/* 脚本任务属性 */}
        {elementType === 'script-task' && (
          <ScriptTaskProperties form={form} />
        )}
        
        {/* 服务任务属性 */}
        {elementType === 'service-task' && (
          <ServiceTaskProperties form={form} />
        )}
        
        {/* 连线属性 */}
        {elementType === 'sequence-flow' && (
          <SequenceFlowProperties form={form} />
        )}
        

      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* 标题栏 */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div 
              className="w-4 h-4 rounded"
              style={{ backgroundColor: elementConfig.color }}
            />
            <span className="font-semibold">{elementConfig.name}</span>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <Button 
            type="primary" 
            icon={<SaveOutlined />} 
            onClick={handleSave}
            size="small"
          >
            保存
          </Button>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={handleDelete}
            size="small"
          >
            删除
          </Button>
        </div>
      </div>
      
      {/* 属性表单 */}
      <div className="flex-1 overflow-y-auto p-4">
        <Form
          form={form}
          layout="vertical"
          size="small"
        >
          {renderAllProperties()}
        </Form>
      </div>
      
      {/* 表单选择器 */}
      <FormSelector
        visible={formSelectorVisible}
        onCancel={() => setFormSelectorVisible(false)}
        onSelect={(selectedForm) => {
          if (Array.isArray(selectedForm)) {
            // 多选模式
            const formKeys = selectedForm.map(form => form.Id).join(',');
            form.setFieldsValue({ formKey: formKeys });
          } else {
            // 单选模式
            form.setFieldsValue({ formKey: selectedForm.Id });
          }
          setFormSelectorVisible(false);
        }}
        selectedFormKey={form.getFieldValue('formKey')}
        multiple={false}
        defaultSelectedForms={[]}
      />
      
      {/* 用户分配选择器 */}
      <UserAssignmentSelector
        visible={userAssignmentVisible}
        value={assignmentConfig}
        onChange={(config) => {
          // 保存新格式的分配配置
          setAssignmentConfig(config);
        }}
        onCancel={() => setUserAssignmentVisible(false)}
        title="配置任务分配"
      />
    </div>
  );
};

export default BpmnPropertyPanel;